import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { CopilotDocument } from '../../../_interfaces/document';
import {
  DetailLevel,
  FormatMode,
  NoteGenerationSettings,
  QuotationPolicy,
} from '../../../_interfaces/note-generation.interface';
import {
  overrideGenerationSettingsForApply,
  resolveDocumentStyleSettings,
  withAbbreviationToggle,
} from '../../../_helpers/note-generation';
import {
  diffStyleSettings,
  styleSettingChangedProps,
  styleSettingsSavedProps,
} from '../../../_helpers/style-settings-analytics';
import { AnalyticsService } from '../../../_services/analytics.service';
import { OptionSliderComponent } from '../../components/option-slider/option-slider.component';
import { OptionSliderOptions } from '../../components/option-slider/option-slider.types';
import { APP_DIALOG_DATA } from '../../components/dialog/dialog.tokens';
import { AppDialogRef } from '../../components/dialog/dialog-ref';
import { DIALOG_IMPORTS } from '../../components/dialog/dialog.imports';

export interface DetailsAndStyleDialogData {
  userSettings?: NoteGenerationSettings | null;
  document: CopilotDocument;
}

export interface DetailsAndStyleResult {
  applied: true;
  config?: NoteGenerationSettings;
}

@Component({
  selector: 'app-details-and-style',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatRadioModule,
    MatSelectModule,
    TranslateModule,
    OptionSliderComponent,
    ...DIALOG_IMPORTS,
  ],
  templateUrl: './details-and-style.component.html',
})
export class DetailsAndStyleComponent implements OnInit, OnDestroy {
  private readonly analytics = inject(AnalyticsService);
  private dismissal: 'none' | 'applied' | 'cancelled' = 'none';
  readonly styleForm: FormGroup;
  readonly seed: NoteGenerationSettings;
  private trackedSettings: NoteGenerationSettings;

  readonly detailLevelOptions: OptionSliderOptions[] = [
    { value: DetailLevel.Brief, label: 'Brief' },
    { value: DetailLevel.Balanced, label: 'Balanced (default)' },
    { value: DetailLevel.Comprehensive, label: 'Comprehensive' },
  ];

  readonly quotationOptions: OptionSliderOptions[] = [
    { value: QuotationPolicy.None, label: 'None' },
    { value: QuotationPolicy.SalientOnly, label: 'Salient only (default)' },
    { value: QuotationPolicy.Liberal, label: 'Liberal' },
  ];

  readonly formatModeOptions: OptionSliderOptions[] = [
    { value: FormatMode.Structured, label: 'Structured' },
    { value: FormatMode.Hybrid, label: 'Hybrid' },
    { value: FormatMode.Prose, label: 'Prose' },
  ];

  constructor(
    @Inject(APP_DIALOG_DATA) public data: DetailsAndStyleDialogData,
    private readonly dialogRef: AppDialogRef<DetailsAndStyleComponent, DetailsAndStyleResult | undefined>,
    private readonly fb: FormBuilder,
  ) {
    this.seed = withAbbreviationToggle(resolveDocumentStyleSettings({
      userSettings: data.userSettings,
      documentSettings: data.document.generationSettings,
      hasCustomSettings: data.document.hasCustomSettings,
    }));
    this.styleForm = this.fb.group({
      detailLevel: [this.seed.detailLevel],
      formatMode: [this.seed.formatMode],
      abbreviationPolicy: [this.seed.abbreviationPolicy],
      quotationPolicy: [this.seed.quotationPolicy],
    });
    this.trackedSettings = { ...this.seed, ...this.styleForm.getRawValue() };
    this.styleForm.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => this.trackFieldChanges(value as NoteGenerationSettings));
  }

  ngOnInit(): void {
    const document = this.data.document;
    this.analytics.track('style_settings_opened', {
      surface: 'document_modal',
      ...(document.templateId != null ? { blueprint_id: String(document.templateId) } : {}),
    });
  }

  ngOnDestroy(): void {
    if (this.dismissal === 'none') {
      this.trackCancelled();
    }
  }

  onSliderChange(controlName: 'detailLevel' | 'quotationPolicy', value: string): void {
    this.styleForm.get(controlName)?.setValue(value);
  }

  cancel(): void {
    this.trackCancelled();
    this.dialogRef.close();
  }

  apply(): void {
    this.dismissal = 'applied';
    const current: NoteGenerationSettings = {
      ...this.seed,
      ...this.styleForm.getRawValue(),
    };
    this.trackSettingsSaved(current);
    const config = overrideGenerationSettingsForApply(this.seed, current);
    const result: DetailsAndStyleResult = { applied: true };
    if (config) {
      result.config = config;
    }
    this.dialogRef.close(result);
  }

  // Analytics helpers

  private trackSettingsSaved(current: NoteGenerationSettings): void {
    const changes = diffStyleSettings(this.seed, current, 'document_modal');
    const modal = this.data.document.templateId != null
      ? { blueprintId: String(this.data.document.templateId) }
      : undefined;
    this.analytics.track('style_settings_saved', styleSettingsSavedProps('document_modal', changes, modal));
  }

  private trackFieldChanges(current: NoteGenerationSettings): void {
    const before = this.trackedSettings;
    const after = { ...before, ...current };
    this.trackedSettings = after;
    const modal = this.data.document.templateId != null
      ? { blueprintId: String(this.data.document.templateId) }
      : undefined;
    for (const change of diffStyleSettings(before, after, 'document_modal')) {
      this.analytics.track('style_setting_changed', styleSettingChangedProps('document_modal', change, modal));
    }
  }

  private trackCancelled(): void {
    if (this.dismissal !== 'none') return;
    this.dismissal = 'cancelled';
    const current: NoteGenerationSettings = {
      ...this.seed,
      ...this.styleForm.getRawValue(),
    };
    const document = this.data.document;
    this.analytics.track('style_settings_cancelled', {
      surface: 'document_modal',
      had_unsaved_changes: diffStyleSettings(this.seed, current, 'document_modal').length > 0,
      ...(document.templateId != null ? { blueprint_id: String(document.templateId) } : {}),
    });
  }
}
